import type { ConversationMessage, ConversationNote } from '@tips-crm/shared';
import type { ConversationArtifact } from '../api/endpoints.js';
import { EmptyState } from './States.js';

type TimelineItem =
  | { kind: 'message'; at: string; data: ConversationMessage }
  | { kind: 'note'; at: string; data: ConversationNote }
  | { kind: 'artifact'; at: string; data: ConversationArtifact };

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function SentimentBadge({ sentiment }: { sentiment?: string | null }) {
  if (!sentiment) return null;
  const s = sentiment.toUpperCase();
  const cls =
    s === 'POSITIVE' ? 'pos' : s === 'NEGATIVE' ? 'neg' : s === 'MIXED' ? 'mixed' : 'neu';
  return <span className={`badge badge--sentiment badge--sentiment-${cls}`}>{s.toLowerCase()}</span>;
}

export function Timeline({
  messages = [],
  notes = [],
  artifacts = [],
}: {
  messages?: ConversationMessage[];
  notes?: ConversationNote[];
  artifacts?: ConversationArtifact[];
}) {
  const items: TimelineItem[] = [
    ...messages.map((m) => ({ kind: 'message' as const, at: m.sent_at, data: m })),
    ...notes.map((n) => ({ kind: 'note' as const, at: n.created_at, data: n })),
    ...artifacts.map((a) => ({ kind: 'artifact' as const, at: a.created_at, data: a })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  if (items.length === 0) {
    return <EmptyState title="Empty timeline" message="No messages, notes, or artifacts yet." />;
  }

  return (
    <ol className="timeline">
      {items.map((item) => {
        if (item.kind === 'message') {
          const m = item.data;
          return (
            <li key={`m-${m.id}`} className={`timeline__item timeline__item--msg-${m.sender_type}`}>
              <div className="timeline__meta">
                <span className="timeline__author">
                  {m.sender_display_name ?? m.sender_type}
                  <SentimentBadge sentiment={m.sentiment} />
                </span>
                <span className="timeline__time">{formatTime(item.at)}</span>
              </div>
              <div className="timeline__body">{m.body}</div>
            </li>
          );
        }
        if (item.kind === 'note') {
          const n = item.data;
          return (
            <li key={`n-${n.id}`} className={`timeline__item timeline__item--note timeline__item--${n.note_type}`}>
              <div className="timeline__meta">
                <span className="timeline__author">
                  <span className="badge badge--note">{n.note_type}</span>
                  {n.author_type === 'system' ? 'System' : 'Agent'}
                </span>
                <span className="timeline__time">{formatTime(item.at)}</span>
              </div>
              <div className="timeline__body">{n.body}</div>
            </li>
          );
        }
        const a = item.data;
        return (
          <li key={`a-${a.id}`} className="timeline__item timeline__item--artifact">
            <div className="timeline__meta">
              <span className="timeline__author">
                <span className="badge badge--artifact">{a.artifact_type}</span>
              </span>
              <span className="timeline__time">{formatTime(item.at)}</span>
            </div>
            <div className="timeline__body">
              {a.url ? (
                <a href={a.url} target="_blank" rel="noreferrer">
                  Open {a.artifact_type}
                </a>
              ) : (
                <span className="muted">{a.artifact_type} (no link available)</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
